/**
 * § F189 (2026-09-26) — A PHONE NEVER RUNS YESTERDAY'S APP.
 *
 * A single-page app loads its JavaScript once. A tab left open on a phone —
 * which is how this family plays — keeps that copy for days, through every
 * deploy. That is how F185 shipped and Jake's replays still came up blank:
 * the guesses were sent by a bundle from before the keystroke tape existed.
 *
 * So every build carries an id (`__BUILD_ID__`, written into `version.json`
 * beside it by `vite.config.ts`). When the app comes back to the foreground,
 * and on every in-app navigation, it asks the server which build is current
 * (at most once a minute, uncached) and, if it is not this one, reloads. A
 * reload is safe at those moments: a head-to-head row in progress is flushed on
 * hide and restored from its tape (§ F187), and nothing else holds unsaved
 * state across a navigation.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare const __BUILD_ID__: string;

const CHECK_EVERY_MS = 60_000;
let lastCheck = 0;

async function reloadIfStale(): Promise<void> {
  const now = Date.now();
  if (now - lastCheck < CHECK_EVERY_MS) return;
  lastCheck = now;
  try {
    const res = await fetch(`/version.json?t=${now}`, { cache: "no-store" });
    if (!res.ok) return;
    const { build } = (await res.json()) as { build?: string };
    if (build && typeof __BUILD_ID__ === "string" && build !== __BUILD_ID__) {
      // Once per target build per tab: if a reload somehow still serves the
      // old app (a cache in the way), it must not become a reload loop.
      const key = "gtw-reloaded-for";
      try {
        if (sessionStorage.getItem(key) === build) return;
        sessionStorage.setItem(key, build);
      } catch {
        return;
      }
      window.location.reload();
    }
  } catch {
    /* offline or mid-deploy: try again next time */
  }
}

export function StaleBuildReload(): null {
  const location = useLocation();
  useEffect(() => {
    void reloadIfStale();
  }, [location.pathname]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void reloadIfStale();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
  return null;
}
